import React, { useEffect, useState } from 'react';
import { Heart, LoaderCircle } from 'lucide-react';
import { ClassData, toggleClassLike } from '../services/api';
import { getCurrentUser } from '../utils/userStorage';
import { mergePersistedEventLike, persistEventLike } from '../utils/eventLikeStorage';

interface EventLikeButtonProps {
  event: ClassData;
  onRequireLogin?: () => void;
  onLikeChange?: (eventId: string, likes: string[]) => void;
  className?: string;
}

const EventLikeButton: React.FC<EventLikeButtonProps> = ({ event, onRequireLogin, onLikeChange, className = '' }) => {
  const [likes, setLikes] = useState<string[]>(event.likes || []);
  const [isSaving, setIsSaving] = useState(false);
  const currentUser = getCurrentUser();
  const userId = String(currentUser?.id || '');
  const eventId = String(event._id || event.title);
  const isLiked = Boolean(userId && likes.includes(String(userId)));

  useEffect(() => {
    setLikes(mergePersistedEventLike(event.likes, eventId, userId));
  }, [event.likes, eventId, userId]);

  const handleLike = async () => {
    if (!userId) {
      onRequireLogin?.();
      return;
    }

    if (!event._id || event.isFallback || event._id.startsWith('mock-')) {
      setLikes((currentLikes) => {
        const isCurrentlyLiked = currentLikes.includes(userId);
        const nextLikes = isCurrentlyLiked ? currentLikes.filter((id) => id !== userId) : [...currentLikes, userId];
        persistEventLike(userId, eventId, !isCurrentlyLiked);
        onLikeChange?.(eventId, nextLikes);
        return nextLikes;
      });
      return;
    }

    try {
      setIsSaving(true);
      const result = await toggleClassLike(event._id);
      setLikes(result.likes);
      persistEventLike(userId, eventId, result.isLiked);
      onLikeChange?.(event._id, result.likes);
    } catch (error: any) {
      alert(error.message || 'No pudimos actualizar el like.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        handleLike();
      }}
      disabled={isSaving}
      aria-pressed={isLiked}
      aria-label={isLiked ? 'Te gusta este evento' : 'Dar like al evento'}
      className={`inline-flex h-11 items-center gap-2 rounded-full border px-3 text-sm font-bold transition disabled:cursor-wait ${isLiked ? 'border-rose-200 bg-rose-50 text-rose-600' : 'border-slate-200 bg-white text-slate-700 hover:border-rose-200 hover:text-rose-600'} ${className}`}
    >
      {isSaving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Heart className={`h-4 w-4 ${isLiked ? 'fill-current' : ''}`} />}
      <span>{likes.length}</span>
    </button>
  );
};

export default EventLikeButton;
